// API configuration for Gaia frontend
import { IS_STANDALONE } from './runtimeProfile.js';

const DEFAULT_NATIVE_BACKEND_URL = 'http://localhost:8090';
const DEFAULT_NATIVE_STT_BASE_URL = 'http://localhost:8001/stt';

const isLoopbackHost = (hostname) => hostname === 'localhost' || hostname === '127.0.0.1';

// Determine if we're running in production (accessed via domain)
const isProduction = !isLoopbackHost(window.location.hostname);

// Capacitor native apps load from http://localhost or capacitor://localhost
// (no port) — they need an absolute backend URL since there's no web server
// to proxy through. We detect this by checking if we're on localhost without
// a port, since Vite dev always runs on a specific port (3000, 5173, etc.).
// A Standalone Install is never Capacitor: the local backend serves the SPA
// itself, so a port-less localhost there must not be mistaken for native.
export const detectCapacitorNative = ({ standalone, location, capacitor }) => {
  if (standalone) {
    return false;
  }
  return capacitor?.isNativePlatform?.() === true ||
    (location.hostname === 'localhost' && !location.port);
};

// Use relative paths - Vite proxy handles routing to backend in development
export const resolveBackendUrl = ({ standalone, env, capacitorNative }) => {
  // Standalone: the local backend serves the SPA, so the API is same-origin
  // whatever VITE_API_BASE_URL leaked into the build environment.
  if (standalone) {
    return '';
  }
  if (env.VITE_API_BASE_URL !== undefined) {
    return env.VITE_API_BASE_URL;
  }
  // Native app needs an absolute URL to reach the backend
  if (capacitorNative) {
    return env.VITE_NATIVE_BACKEND_URL || DEFAULT_NATIVE_BACKEND_URL;
  }
  // Empty string = use relative URLs, which Vite proxies to backend
  return '';
};

const normalizeWsBase = (rawValue, location) => {
  if (!rawValue) {
    return '';
  }

  let value = String(rawValue).trim();
  if (!value) {
    return '';
  }

  if (value.startsWith('//')) {
    value = `${location.protocol}${value}`;
  }

  try {
    const url = new URL(value, location.origin);
    if (url.protocol === 'http:') {
      url.protocol = 'ws:';
    } else if (url.protocol === 'https:') {
      url.protocol = 'wss:';
    }
    return url.toString().replace(/\/$/, '');
  } catch {
    if (value.startsWith('http://')) {
      return `ws://${value.slice('http://'.length).replace(/\/$/, '')}`;
    }
    if (value.startsWith('https://')) {
      return `wss://${value.slice('https://'.length).replace(/\/$/, '')}`;
    }
    return value.replace(/\/$/, '');
  }
};

const sameOriginWsBase = (location) => {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}`;
};

export const resolveWsUrl = ({ standalone, env, location, capacitorNative }) => {
  if (standalone) {
    return sameOriginWsBase(location);
  }
  const explicitWs = env.VITE_WS_BASE_URL;
  if (explicitWs) {
    return normalizeWsBase(explicitWs, location);
  }
  const apiBase = env.VITE_API_BASE_URL;
  if (apiBase) {
    return normalizeWsBase(apiBase, location);
  }
  // Native app needs absolute WebSocket URL (same backend as HTTP)
  if (capacitorNative) {
    const nativeBackend = env.VITE_NATIVE_BACKEND_URL || DEFAULT_NATIVE_BACKEND_URL;
    return normalizeWsBase(nativeBackend, location);
  }
  // Use current origin - Vite proxy handles routing to backend in dev
  return sameOriginWsBase(location);
};

// STT service - always use /stt prefix (dev proxies to 8001, prod uses cloudflared)
export const resolveSttUrl = ({ standalone, env, location, capacitorNative }) => {
  // Standalone runs no STT service and hides voice input (IS_STANDALONE), so
  // this is never dialled; it stays same-origin so nothing points off-machine.
  if (standalone) {
    return `${location.origin}/stt`;
  }
  if (env.VITE_STT_BASE_URL) {
    return env.VITE_STT_BASE_URL;
  }
  // Always use /stt prefix - backend or cloudflared handles routing
  // Note: The /stt prefix is required for all endpoints
  if (!isLoopbackHost(location.hostname)) return '';
  // Native WebView has no Vite proxy - needs an absolute STT URL, mirroring
  // resolveBackendUrl's native handling.
  if (capacitorNative) {
    return env.VITE_NATIVE_STT_BASE_URL || DEFAULT_NATIVE_STT_BASE_URL;
  }
  // Dev: same-origin /stt rides the Vite proxy to THIS worktree's STT
  // container (hardcoding localhost:8001 only reached the slot-0 stack).
  return `${location.origin}/stt`;
};

const runtime = {
  standalone: IS_STANDALONE,
  env: import.meta.env,
  location: window.location,
};
runtime.capacitorNative = detectCapacitorNative({ ...runtime, capacitor: window.Capacitor });

export const API_CONFIG = {
  // Base URLs
  BACKEND_URL: resolveBackendUrl(runtime),
  WS_BASE_URL: resolveWsUrl(runtime),
  STT_BASE_URL: resolveSttUrl(runtime),
  
  // API endpoints
  get HEALTH_ENDPOINT() {
    return `${this.BACKEND_URL}/api/health`;
  },
  
  get CHAT_ENDPOINT() {
    return `${this.BACKEND_URL}/api/chat`;
  },
  
  get TEST_ENDPOINT() {
    return `${this.BACKEND_URL}/api/test`;
  },
  
  get TTS_ENDPOINT() {
    return `${this.BACKEND_URL}/api/tts`;
  },
  
  get CAMPAIGNS_ENDPOINT() {
    return `${this.BACKEND_URL}/api/campaigns`;
  },
  
  get IMAGES_ENDPOINT() {
    return `${this.BACKEND_URL}/api/images`;
  },
  
  // WebSocket endpoints
  get WS_CHAT_STREAM() {
    return `${this.WS_BASE_URL}/api/chat/stream`;
  },
  
  get VOICE_ACTIVITY_ENDPOINT() {
    // Use STT_BASE_URL when set (handles Firebase Hosting where no nginx proxy exists)
    if (this.STT_BASE_URL) {
      return `${this.STT_BASE_URL}/voice-activity`;
    }
    return isProduction ? '/stt/voice-activity' : 'http://localhost:8001/stt/voice-activity';
  },
  
  // Configuration options
  REQUEST_TIMEOUT: 30000, // 30 seconds
  WS_RECONNECT_DELAY: 5000, // 5 seconds
  MAX_RETRIES: 3,
  
  // Feature flags
  ENABLE_AUDIO: import.meta.env.VITE_ENABLE_AUDIO !== 'false',
  USE_OPENAPI: true,  // Always use OpenAPI now that protobuf is removed
  // Auto-submit player turns: when enabled, player submissions go directly to backend
  // instead of requiring DM approval. Useful for solo playtesting from player view.
  AUTO_SUBMIT_PLAYER_TURNS: import.meta.env.VITE_AUTO_SUBMIT_PLAYER_TURNS === 'true',

  // Submit ack: when enabled, submit_turn uses emitWithAck for server confirmation
  ENABLE_SUBMIT_ACK: import.meta.env.VITE_ENABLE_SUBMIT_ACK !== 'false',

  // V3 dialog action: when enabled, the player UI surfaces "Talk to <NPC>"
  // affordances that submit a turn with route="dialog" → backend dispatches
  // to dialog_turn_runner which streams a Doppelganger reply and skips the
  // heavy analyze/scene-flow/persist stages. Dark-launched off by default.
  ENABLE_DIALOG_ACTION: import.meta.env.VITE_ENABLE_DIALOG_ACTION === 'true',

  // Cinematic 3D scene diorama (BOU-1907): when enabled, the desktop PlayerViewV2
  // renders the current scene as a depth-displaced 3D backdrop (opt-in per campaign
  // via the enable_scene_diorama setting). Global kill-switch, dark-launched off —
  // the per-campaign setting only takes effect when this is on.
  ENABLE_SCENE_DIORAMA: import.meta.env.VITE_ENABLE_SCENE_DIORAMA === 'true',

  // BOU-3069 Layer 8: observe the backend's `input_ready` event (durable-answer
  // marker) without changing behaviour — telemetry/log only. Stage one.
  INPUT_READY_TELEMETRY: import.meta.env.VITE_INPUT_READY_TELEMETRY === 'true',

  // Stage two: let `input_ready` re-enable the composer, instead of waiting for
  // `turn_complete` (which lands only after the whole post-answer tail runs).
  // `turn_complete` still finalises streaming/card state either way.
  INPUT_READY_UNLOCKS_COMPOSER: import.meta.env.VITE_INPUT_READY_UNLOCKS_COMPOSER === 'true',

  // Debug mode
  DEBUG: import.meta.env.VITE_DEBUG === 'true' || import.meta.env.MODE === 'development',

};

/**
 * Prefix a relative API path with the configured backend URL.
 * Use instead of hardcoded fetch('/api/...') calls so requests
 * work when the frontend is served from a different origin (e.g. Firebase Hosting).
 *
 * Usage: fetch(apiUrl('/api/auth/registration-status'), { ... })
 */
export const apiUrl = (path) => `${API_CONFIG.BACKEND_URL}${path}`;
